import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";

import PageContent from "ui/PageContent";
import Panel from "ui/Panel";

import { useDateRange } from "providers/WithDateRange";
import { Data, Datum, SeriesConfig } from "ui/Graph/util";
import Loading from "ui/Loading";
import DateRangeBar from "ui/DateRangeBar";
import GraphSection from "ui/Graph/GraphSection";
import { AreaSeries } from "ui/Graph/Series";
import DateRangeGraph from "ui/Graph/DateRangeGraph";

import {
  ServerQueryOverview as ServerQueryOverviewType,
  ServerQueryOverviewVariables,
} from "./types/ServerQueryOverview";
import Table from "./Table";
import QUERY from "./Query.graphql";

const ServerQueryOverview: React.FunctionComponent = () => {
  const { serverId } = useParams();
  const { from, to } = useDateRange();

  const { data, loading, error } = useQuery<
    ServerQueryOverviewType,
    ServerQueryOverviewVariables
  >(QUERY, {
    variables: {
      serverId,
      startTs: from.unix(),
      endTs: to.unix(),
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  return (
    <PageContent
      title="Query Performance"
      pageControls={<DateRangeBar />}
      pageCategory="queries"
      pageName="index_server"
    >
      <Panel title="Database Queries">
        <Graph data={data} />
        <Table
          data={data.getQueryOverviewStatsByDatabase.filter(
            (d) => !!d.database,
          )}
        />
      </Panel>
    </PageContent>
  );
};

const Graph: React.FunctionComponent<{ data: ServerQueryOverviewType }> = ({
  data,
}) => {
  const noData = data.getQueryOverviewStatsByDatabase == null;
  const graphData: Data = {};
  const series: SeriesConfig[] = [];

  if (data?.getQueryOverviewStatsByDatabase) {
    data.getQueryOverviewStatsByDatabase.forEach((d) => {
      const key = d.database?.id ?? "other";
      graphData[key] = d.queriesPerSecondSeries as unknown as Datum[];
      series.push({
        key: key,
        label: d.database?.datname ?? "Other databases",
        type: AreaSeries,
      });
    });
  }

  return (
    <GraphSection noData={noData}>
      <DateRangeGraph
        data={graphData}
        axes={{
          left: {
            format: "count",
            tipFormat: (y) => y.toFixed(2) + " QPS",
          },
        }}
        series={series}
      />
    </GraphSection>
  );
};

export default ServerQueryOverview;
